import React, { useEffect, useMemo, useState } from "react";
import { Plus, Pencil, Trash2, Wallet, RefreshCw } from "lucide-react";
import { useAuth } from "../../hooks/useAuth";
import { useAppState } from "../../hooks/useAppState";
import {
  subscribeCosts,
  addCost,
  updateCost,
  deleteCost,
  annualize,
} from "../../services/costsService";

const FREQUENZE = [
  { value: "mensile", label: "Mensile" },
  { value: "annuale", label: "Annuale" },
  { value: "una-tantum", label: "Una tantum" },
];
const CATEGORIE = [
  { value: "software", label: "Software" },
  { value: "servizi", label: "Servizi" },
  { value: "altro", label: "Altro" },
];

const EMPTY = {
  nome: "",
  importo: "",
  frequenza: "mensile",
  categoria: "software",
  ribaltato: false,
  clientId: null,
  prossimoRinnovo: "",
};

function formatEuro(v) {
  return new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(Number(v) || 0);
}
function formatDateIT(iso) {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  return y && m && d ? `${d}/${m}/${y}` : iso;
}

// Ricava l'anno di creazione di un costo, gestendo sia Timestamp Firestore
// (con .toDate()/.seconds) sia stringhe ISO (fallback locale prima della sync).
function getCreatedYear(cost) {
  const ts = cost?.createdAt;
  if (!ts) return null;
  if (typeof ts.toDate === "function") return ts.toDate().getFullYear();
  if (typeof ts.seconds === "number") return new Date(ts.seconds * 1000).getFullYear();
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? null : d.getFullYear();
}

export default function CostiSection({ darkMode = false, selectedYear }) {
  const { user } = useAuth();
  const { clients = [] } = useAppState();
  const [costs, setCosts] = useState([]);
  const year = selectedYear || new Date().getFullYear();
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [clientQuery, setClientQuery] = useState(""); // testo visibile del combobox cliente
  const [saving, setSaving] = useState(false);

  const clientNameById = (id) => clients.find((c) => c.id === id)?.name || null;

  useEffect(() => {
    if (!user) {
      setCosts([]);
      return undefined;
    }
    const unsub = subscribeCosts(user.uid, setCosts);
    return () => unsub();
  }, [user]);

  // Filtra i costi per l'anno selezionato nella dashboard. I costi non hanno un
  // campo "anno" esplicito: i ricorrenti (mensile/annuale) valgono da quando sono
  // stati creati in avanti; gli una-tantum contano solo nell'anno in cui sono
  // stati sostenuti (prossimo rinnovo se presente, altrimenti data di creazione).
  const costsAnno = useMemo(() => {
    return costs.filter((c) => {
      if (c.frequenza === "una-tantum") {
        const refYear = c.prossimoRinnovo
          ? new Date(c.prossimoRinnovo).getFullYear()
          : getCreatedYear(c);
        return refYear === null ? true : refYear === year;
      }
      const createdYear = getCreatedYear(c);
      return createdYear === null || createdYear <= year;
    });
  }, [costs, year]);

  const summary = useMemo(() => {
    const sostenuti = costsAnno.reduce((s, c) => s + annualize(c), 0);
    const ribaltati = costsAnno.filter((c) => c.ribaltato).reduce((s, c) => s + annualize(c), 0);
    const netto = sostenuti - ribaltati;
    const pct = sostenuti > 0 ? Math.round((ribaltati / sostenuti) * 100) : 0;
    return { sostenuti, ribaltati, netto, pct };
  }, [costsAnno]);

  const openAdd = () => { setEditingId(null); setForm(EMPTY); setClientQuery(""); setShowForm(true); };
  const openEdit = (c) => {
    setEditingId(c.id);
    setForm({
      ...EMPTY,
      nome: c.nome || "",
      importo: String(c.importo ?? ""),
      frequenza: c.frequenza || "mensile",
      categoria: c.categoria || "altro",
      ribaltato: Boolean(c.ribaltato),
      clientId: c.clientId || null,
      prossimoRinnovo: c.prossimoRinnovo || "",
    });
    setClientQuery(clientNameById(c.clientId) || "");
    setShowForm(true);
  };
  const closeForm = () => { setShowForm(false); setEditingId(null); setForm(EMPTY); setClientQuery(""); };

  // Combobox cliente: risolve il nome digitato/selezionato in clientId (null se nessun match).
  const handleClientInput = (val) => {
    setClientQuery(val);
    const match = clients.find((c) => (c.name || "").trim().toLowerCase() === val.trim().toLowerCase());
    setForm((f) => ({ ...f, clientId: match ? match.id : null }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user || !form.nome.trim() || !(Number(form.importo) > 0)) return;
    setSaving(true);
    try {
      if (editingId) await updateCost(editingId, form);
      else await addCost(user.uid, form);
      closeForm();
    } catch (err) {
      console.error("Errore salvataggio costo:", err);
    } finally {
      setSaving(false);
    }
  };

  const card = darkMode ? "bg-neutral-900 border-neutral-800" : "bg-white border-neutral-200";
  const input = `w-full px-3 py-2 rounded-lg border text-sm ${darkMode ? "bg-neutral-800 border-neutral-700 text-white" : "bg-white border-neutral-300 text-neutral-900"} focus:outline-none focus:ring-2 focus:ring-forfy-500`;
  const label = `block text-xs font-medium mb-1 ${darkMode ? "text-neutral-400" : "text-neutral-600"}`;

  return (
    <section className={`mt-6 rounded-xl border p-5 ${card}`}>
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <Wallet className={`w-5 h-5 ${darkMode ? "text-forfy-300" : "text-forfy-600"}`} />
          <h2 className={`text-sm font-semibold uppercase tracking-wide ${darkMode ? "text-white" : "text-neutral-900"}`}>
            Costi ribaltabili
          </h2>
        </div>
        <button
          onClick={openAdd}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-forfy-500 text-white text-sm font-medium hover:bg-forfy-600 transition-colors"
        >
          <Plus size={16} /> Aggiungi costo
        </button>
      </div>
      <p className={`text-sm mb-4 ${darkMode ? "text-neutral-500" : "text-neutral-600"}`}>
        Costi ricorrenti e quanto ne ribalti ai clienti (totali su base annua) — {year}.
      </p>

      {/* Riepilogo */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
        <div className={`rounded-xl p-4 ${darkMode ? "bg-neutral-800/60" : "bg-neutral-50"}`}>
          <p className={`text-xs ${darkMode ? "text-neutral-500" : "text-neutral-500"}`}>Costi sostenuti / anno</p>
          <p className={`text-xl font-bold mt-1 ${darkMode ? "text-white" : "text-neutral-900"}`}>{formatEuro(summary.sostenuti)}</p>
        </div>
        <div className={`rounded-xl p-4 ${darkMode ? "bg-emerald-900/20" : "bg-emerald-50"}`}>
          <p className={`text-xs ${darkMode ? "text-emerald-400" : "text-emerald-700"}`}>Costi ribaltati / anno</p>
          <p className="text-xl font-bold mt-1 text-emerald-600">{formatEuro(summary.ribaltati)}</p>
          <p className={`text-xs mt-0.5 ${darkMode ? "text-neutral-500" : "text-neutral-500"}`}>{summary.pct}% ribaltato</p>
        </div>
        <div className={`rounded-xl p-4 ${darkMode ? "bg-neutral-800/60" : "bg-neutral-50"}`}>
          <p className={`text-xs ${darkMode ? "text-neutral-500" : "text-neutral-500"}`}>Netto a tuo carico</p>
          <p className={`text-xl font-bold mt-1 ${darkMode ? "text-white" : "text-neutral-900"}`}>{formatEuro(summary.netto)}</p>
        </div>
      </div>

      {/* Form add/edit */}
      {showForm && (
        <form onSubmit={handleSubmit} className={`rounded-xl border p-4 mb-4 ${darkMode ? "border-neutral-700 bg-neutral-800/40" : "border-neutral-200 bg-neutral-50"}`}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className={label}>Nome *</label>
              <input className={input} value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} placeholder="Es. Hosting, Software X" />
            </div>
            <div>
              <label className={label}>Importo (€) *</label>
              <input type="number" step="0.01" min="0" className={input} value={form.importo} onChange={(e) => setForm({ ...form, importo: e.target.value })} placeholder="0,00" />
            </div>
            <div>
              <label className={label}>Frequenza</label>
              <select className={input} value={form.frequenza} onChange={(e) => setForm({ ...form, frequenza: e.target.value })}>
                {FREQUENZE.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
              </select>
            </div>
            <div>
              <label className={label}>Categoria</label>
              <select className={input} value={form.categoria} onChange={(e) => setForm({ ...form, categoria: e.target.value })}>
                {CATEGORIE.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className={label}>Prossimo rinnovo</label>
              <input type="date" className={input} value={form.prossimoRinnovo} onChange={(e) => setForm({ ...form, prossimoRinnovo: e.target.value })} />
            </div>
            <label className={`flex items-center gap-2 text-sm mt-6 ${darkMode ? "text-neutral-300" : "text-neutral-700"}`}>
              <input
                type="checkbox"
                checked={form.ribaltato}
                onChange={(e) => {
                  const on = e.target.checked;
                  if (!on) setClientQuery("");
                  setForm((f) => ({ ...f, ribaltato: on, clientId: on ? f.clientId : null }));
                }}
              />
              Ribaltato al cliente
            </label>
            {form.ribaltato && (
              <div className="sm:col-span-2">
                <label className={label}>Cliente (opzionale)</label>
                <input
                  className={input}
                  list="costi-clienti-list"
                  value={clientQuery}
                  onChange={(e) => handleClientInput(e.target.value)}
                  placeholder="Nessun cliente specifico"
                />
                <datalist id="costi-clienti-list">
                  {clients.map((c) => (
                    <option key={c.id} value={c.name} />
                  ))}
                </datalist>
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <button type="button" onClick={closeForm} className={`px-3 py-1.5 rounded-lg text-sm ${darkMode ? "bg-neutral-800 text-neutral-300 hover:bg-neutral-700" : "bg-neutral-100 text-neutral-700 hover:bg-neutral-200"}`}>Annulla</button>
            <button type="submit" disabled={saving} className="px-3 py-1.5 rounded-lg text-sm font-medium bg-forfy-500 text-white hover:bg-forfy-600 disabled:opacity-60">
              {saving ? "..." : editingId ? "Salva" : "Aggiungi"}
            </button>
          </div>
        </form>
      )}

      {/* Lista */}
      {costsAnno.length === 0 ? (
        <div className={`rounded-xl border border-dashed p-6 text-center text-sm ${darkMode ? "border-neutral-700 text-neutral-500" : "border-neutral-300 text-neutral-400"}`}>
          {costs.length === 0
            ? "Nessun costo registrato. Aggiungi il primo per iniziare a tracciare cosa ribalti ai clienti."
            : `Nessun costo per l'anno ${year}.`}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className={darkMode ? "bg-neutral-800/50" : "bg-neutral-50"}>
                {["Nome", "Categoria", "Importo", "Ribaltato a", "Rinnovo", ""].map((h, i) => (
                  <th key={i} className={`px-3 py-2 text-left font-medium ${darkMode ? "text-neutral-400" : "text-neutral-500"} ${i === 2 ? "text-right" : ""}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {costsAnno.map((c) => (
                <tr key={c.id} className={`border-t ${darkMode ? "border-neutral-800" : "border-neutral-100"}`}>
                  <td className={`px-3 py-2.5 ${darkMode ? "text-neutral-200" : "text-neutral-800"}`}>{c.nome}</td>
                  <td className="px-3 py-2.5">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${darkMode ? "bg-neutral-800 text-neutral-400" : "bg-neutral-100 text-neutral-600"}`}>
                      {CATEGORIE.find((x) => x.value === c.categoria)?.label || c.categoria}
                    </span>
                  </td>
                  <td className={`px-3 py-2.5 text-right tabular-nums ${darkMode ? "text-neutral-300" : "text-neutral-700"}`}>
                    {formatEuro(c.importo)}
                    <span className={`ml-1 text-xs ${darkMode ? "text-neutral-500" : "text-neutral-400"}`}>
                      /{FREQUENZE.find((f) => f.value === c.frequenza)?.label.toLowerCase() || c.frequenza}
                    </span>
                  </td>
                  <td className="px-3 py-2.5">
                    {c.ribaltato ? (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium">
                        {clientNameById(c.clientId) || "Non specificato"}
                      </span>
                    ) : (
                      <span className={`text-xs ${darkMode ? "text-neutral-600" : "text-neutral-400"}`}>—</span>
                    )}
                  </td>
                  <td className={`px-3 py-2.5 ${darkMode ? "text-neutral-400" : "text-neutral-500"}`}>
                    <span className="inline-flex items-center gap-1">
                      {c.frequenza !== "una-tantum" && <RefreshCw size={12} className="opacity-60" />}
                      {formatDateIT(c.prossimoRinnovo)}
                    </span>
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => openEdit(c)} aria-label="Modifica" className={`p-1.5 rounded-lg ${darkMode ? "hover:bg-neutral-800 text-neutral-400" : "hover:bg-neutral-100 text-neutral-500"}`}><Pencil size={14} /></button>
                      <button onClick={() => { if (window.confirm("Eliminare questo costo?")) deleteCost(c.id); }} aria-label="Elimina" className={`p-1.5 rounded-lg ${darkMode ? "hover:bg-neutral-800 text-neutral-400" : "hover:bg-neutral-100 text-neutral-500"}`}><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
